import {
  type RequestQuery,
  type ResponseData,
  useApiQuery,
} from '@vrplatform/data';
import type { party_enum } from '@vrplatform/graphql';
import { day, sortBy, toCamelCase } from '@vrplatform/utils';
import { useMemo } from 'react';
import { useTrustReconciliationByListingTableFilter } from './useTrustReconciliationByListingTableFilter';
import { populateTrustReconciliationByListingRowParties } from './useTrustReconciliationByListingTableQuery.utils';

export const STORYBOOK_TRUST_RECONCILIATION_BY_LISTING_TABLE_QUERY_KEY =
  'trust-reconciliation-by-listing-table';

type ApiLegacyReportRow =
  ResponseData<'get:/reports/trust-reconciliation/by-listing'>['trustReconciliationByListing']['groups'][0];

type ApiAccountRow = {
  id: string;
  name: string;
  categoryId: string;
  total: number;
};

type ApiListingOwnershipRow = {
  id: string;
  listingId: string | null;
  ownershipPeriodId: string | null;
  name: string;
  total: number;
  accounts?: ApiAccountRow[];
};

type ApiSectionRow = {
  name: string;
  total: number;
  type?: string;
  accounts?: ApiAccountRow[];
  listingOwnerships?: ApiListingOwnershipRow[];
};

type ApiListingRow = {
  id: string;
  listingId: string | null;
  ownershipPeriodId: string | null;
  name: string;
  total: number;
  sections?: ApiSectionRow[];
};

type ApiLegacyAccountsRow = {
  name: string;
  total: number;
  accounts?: ApiAccountRow[];
};

type ApiLegacyListingsRow = {
  name: string;
  total: number;
  listings?: ApiListingRow[];
};

type ApiReportRow =
  | ApiLegacyReportRow
  | ApiLegacyAccountsRow
  | ApiLegacyListingsRow
  | ApiListingRow;

export type TrustReconciliationByListingTableQueryRow = {
  id: string;
  name: string;
  centTotal: number;
  type: 'listing' | 'section' | 'listingOwnership' | 'account';
  categoryId?: string;
  listingOwnershipPeriodId?: string;
  accountId?: string;
  party: party_enum | undefined;
  subRows: TrustReconciliationByListingTableQueryRow[];
};

export type TrustReconciliationByListingTableQuerySorting =
  | 'name'
  | 'centTotal';

const formatAccountRow = (
  account: ApiAccountRow,
  parentId: string,
  listingOwnershipPeriodId?: string
): TrustReconciliationByListingTableQueryRow => ({
  id: `${parentId}_${account.id}`,
  name: account.name,
  type: 'account',
  categoryId: account.categoryId,
  accountId: account.id,
  listingOwnershipPeriodId,
  party: undefined,
  centTotal: account.total,
  subRows: [],
});

const formatSectionRow = (
  section: ApiSectionRow,
  parentId: string,
  listingOwnershipPeriodId?: string
): TrustReconciliationByListingTableQueryRow => {
  const sectionId = `${parentId}_${toCamelCase(section.name)}`;
  const listingOwnerships = section.listingOwnerships ?? [];
  const accounts = section.accounts ?? [];

  const subRows =
    listingOwnerships.length > 0
      ? listingOwnerships.map((ownership) =>
          formatListingOwnershipRow(ownership, sectionId)
        )
      : accounts.map((account) =>
          formatAccountRow(account, sectionId, listingOwnershipPeriodId)
        );

  return {
    id: sectionId,
    name: section.name,
    type: 'section',
    listingOwnershipPeriodId,
    party: undefined,
    centTotal: section.total,
    subRows,
  };
};

const formatListingOwnershipRow = (
  ownership: ApiListingOwnershipRow,
  parentId: string
): TrustReconciliationByListingTableQueryRow => {
  const ownershipId = `${parentId}_${ownership.id}`;
  const accounts = ownership.accounts ?? [];
  const listingOwnershipPeriodId =
    ownership.ownershipPeriodId ?? ownership.listingId ?? undefined;

  return {
    id: ownershipId,
    name: ownership.name,
    type: 'listingOwnership',
    listingOwnershipPeriodId,
    party: undefined,
    centTotal: ownership.total,
    subRows: accounts.map((account) =>
      formatAccountRow(account, ownershipId, listingOwnershipPeriodId)
    ),
  };
};

const formatListingRow = (
  listing: ApiListingRow,
  parentId?: string
): TrustReconciliationByListingTableQueryRow => {
  const listingOwnershipPeriodId =
    listing.ownershipPeriodId ?? listing.listingId ?? undefined;
  const rowId = parentId ? `${parentId}_${listing.id}` : listing.id;
  const sections = listing.sections ?? [];

  return {
    id: rowId,
    name: listing.name,
    type: 'listing',
    listingOwnershipPeriodId,
    party: undefined,
    centTotal: listing.total,
    subRows: sections.map((section) =>
      formatSectionRow(section, rowId, listingOwnershipPeriodId)
    ),
  };
};

const isListingRow = (row: ApiReportRow): row is ApiListingRow => {
  return (
    typeof row === 'object' &&
    row !== null &&
    'id' in row &&
    'name' in row &&
    'total' in row
  );
};

const isLegacyAccountsRow = (
  row: ApiReportRow
): row is ApiLegacyAccountsRow => {
  return (
    typeof row === 'object' &&
    row !== null &&
    'accounts' in row &&
    Array.isArray((row as { accounts?: unknown }).accounts)
  );
};

const isLegacyListingsRow = (
  row: ApiReportRow
): row is ApiLegacyListingsRow => {
  return (
    typeof row === 'object' &&
    row !== null &&
    'listings' in row &&
    Array.isArray((row as { listings?: unknown }).listings)
  );
};

const formatReportRow = (
  row: ApiReportRow,
  rowIndex: number
): TrustReconciliationByListingTableQueryRow => {
  if (isListingRow(row)) {
    return formatListingRow(row);
  }

  const rowIdBase =
    typeof row === 'object' && row !== null && 'name' in row
      ? toCamelCase(String(row.name))
      : 'unknown';
  const rowId = `${rowIdBase}_${rowIndex}`;

  if (isLegacyAccountsRow(row)) {
    const accounts = row.accounts ?? [];

    return {
      id: rowId,
      name: row.name,
      type: 'section',
      party: undefined,
      centTotal: row.total,
      subRows: accounts.map((account) => formatAccountRow(account, rowId)),
    };
  }

  if (isLegacyListingsRow(row)) {
    const listings = row.listings ?? [];

    return {
      id: rowId,
      name: row.name,
      type: 'section',
      party: undefined,
      centTotal: row.total,
      subRows: listings.map((listing) => formatListingRow(listing, rowId)),
    };
  }

  return {
    id: rowId,
    name: 'Unknown',
    type: 'section',
    party: undefined,
    centTotal: 0,
    subRows: [],
  };
};

const sortRows = (
  rows: TrustReconciliationByListingTableQueryRow[],
  sorting: TrustReconciliationByListingTableQuerySorting
) => {
  const direction = sorting === 'centTotal' ? 'desc' : 'asc';
  const balanceSectionNames = new Set(['manager balances', 'owner balances']);

  const isBalancesSection = (row: TrustReconciliationByListingTableQueryRow) =>
    row.type === 'section' &&
    balanceSectionNames.has(row.name.trim().toLowerCase());

  const isUnassignedListingRow = (
    row: TrustReconciliationByListingTableQueryRow
  ) => {
    if (row.type !== 'listing') return false;

    const normalizedId = row.id.trim().toLowerCase();
    const normalizedName = row.name.trim().toLowerCase();

    return (
      normalizedId === 'unassigned' ||
      normalizedName === 'unassigned' ||
      normalizedName === 'unassigned listing'
    );
  };

  const sortRow = (
    row: TrustReconciliationByListingTableQueryRow
  ): TrustReconciliationByListingTableQueryRow => {
    if (row.subRows.length === 0) return row;

    const sortedSubRows = row.subRows.map(sortRow);
    const childType = row.subRows[0]?.type;

    if (
      childType === 'account' ||
      childType === 'listingOwnership' ||
      childType === 'listing'
    ) {
      const sorted = sortBy(sortedSubRows, sorting, direction);

      if (childType === 'listing' && isBalancesSection(row)) {
        const unassignedListings = sorted.filter(isUnassignedListingRow);

        if (unassignedListings.length > 0) {
          const assignedListings = sorted.filter(
            (subRow) => !isUnassignedListingRow(subRow)
          );

          return {
            ...row,
            subRows: [...unassignedListings, ...assignedListings],
          };
        }
      }

      return {
        ...row,
        subRows: sorted,
      };
    }

    return {
      ...row,
      subRows: sortedSubRows,
    };
  };

  const sortedRows = rows.map(sortRow);

  return rows[0]?.type === 'listing'
    ? sortBy(sortedRows, sorting, direction)
    : sortedRows;
};

export const getTrustReconciliationByListingQueryFilterParams = (
  filter: ReturnType<
    typeof useTrustReconciliationByListingTableFilter
  >['filter']
): RequestQuery<'get:/reports/trust-reconciliation/by-listing'> => {
  return {
    endDate: day(filter.endDate).yyyymmdd(),
  };
};

export const useTrustReconciliationByListingTableQuery = (
  sorting: TrustReconciliationByListingTableQuerySorting
) => {
  const { filter } = useTrustReconciliationByListingTableFilter();

  const query = useApiQuery(
    async ($api, signal, args) => {
      const result = await $api.GET(
        '/reports/trust-reconciliation/by-listing',
        {
          params: {
            query: getTrustReconciliationByListingQueryFilterParams(
              args.filter
            ),
          },
          signal,
        }
      );

      if (result.error) throw result.error;

      const parentRow = result.data.trustReconciliationByListing;

      return {
        rows: populateTrustReconciliationByListingRowParties([
          {
            centTotal: parentRow.total,
            id: 'trust-reconciliation-by-listing-report',
            name: parentRow.name,
            party: undefined,
            subRows: parentRow.groups.map((row, rowIndex) =>
              formatReportRow(row, rowIndex)
            ),
            type: 'section',
          },
        ] satisfies TrustReconciliationByListingTableQueryRow[]),
      };
    },
    {
      storybookQueryKey:
        STORYBOOK_TRUST_RECONCILIATION_BY_LISTING_TABLE_QUERY_KEY,
      queryGroup: 'reports',
      uniqueQueryKey: 'trust-reconciliation-by-listing-report',
      queryKeys: ['journalEntries', 'reports'],
      variables: {
        filter,
      },
    }
  );

  const data = useMemo(() => {
    if (!query.data) return undefined;

    return {
      ...query.data,
      rows: sortRows(query.data.rows, sorting),
    };
  }, [query.data, sorting]);

  return {
    ...query,
    data,
  };
};
